// MfaStep.js - Segundo passo do login (verificação em dois passos)
//
// Aparece depois da password certa, em dois modos:
//
//   verificar   - já tem MFA: pede o código da app (ou um de recuperação)
//   configurar  - ainda não tem: mostra o QR code, confirma com o primeiro
//                 código e mostra os códigos de recuperação UMA vez
//
// O MFA é obrigatório para todos, por isso o modo "configurar" não tem
// botão de saltar: só se entra depois de o ter.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, AlertCircle, Copy, Download, KeyRound, ArrowLeft } from 'lucide-react';
import apiService from './apiService';
import './MfaStep.css';

function MfaStep({ modo, challenge, onDone, onRestart }) {
  const { t } = useTranslation();
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [aEnviar, setAEnviar] = useState(false);
  const [usarRecuperacao, setUsarRecuperacao] = useState(false);

  // modo configurar
  const [qr, setQr] = useState(null);
  const [segredo, setSegredo] = useState('');
  const [mostrarSegredo, setMostrarSegredo] = useState(false);
  const [codigosRecuperacao, setCodigosRecuperacao] = useState(null);
  const [sessao, setSessao] = useState(null);
  const [guardou, setGuardou] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const tratarErro = (e) => {
    if (e.reiniciar) {
      onRestart(e.message);
      return;
    }
    setErro(e.message);
    setCodigo('');
  };

  useEffect(() => {
    if (modo !== 'configurar') return;
    apiService.mfaSetupStart(challenge)
      .then((d) => { setQr(d.qr); setSegredo(d.secret); })
      .catch(tratarErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, challenge]);

  const enviar = async () => {
    if (!codigo.trim() || aEnviar) return;
    setAEnviar(true);
    setErro('');
    try {
      if (modo === 'verificar') {
        onDone(await apiService.mfaVerify(challenge, codigo));
      } else {
        const d = await apiService.mfaSetupConfirm(challenge, codigo);
        // A sessão já está aberta, mas só se segue depois de confirmar que
        // guardou os códigos: são mostrados uma única vez.
        setSessao(d);
        setCodigosRecuperacao(d.recovery_codes || []);
      }
    } catch (e) {
      tratarErro(e);
    } finally {
      setAEnviar(false);
    }
  };

  const textoDosCodigos = () =>
    `${t('mfa.ficheiroTitulo')}\n\n${(codigosRecuperacao || []).join('\n')}\n\n${t('mfa.ficheiroNota')}\n`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(textoDosCodigos());
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* sem permissão de clipboard: fica o botão de descarregar */
    }
  };

  const descarregar = () => {
    const blob = new Blob([textoDosCodigos()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'smart-home-codigos-recuperacao.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------------------------------------- códigos de recuperação (fim)
  if (codigosRecuperacao) {
    return (
      <div className="login-container">
        <div className="login-box mfa-box">
          <div className="mfa-cabecalho">
            <KeyRound size={28} />
            <h2>{t('mfa.recuperacaoTitulo')}</h2>
          </div>
          <p className="mfa-texto">{t('mfa.recuperacaoTexto')}</p>

          <div className="mfa-codigos">
            {codigosRecuperacao.map((c) => <code key={c}>{c}</code>)}
          </div>

          <div className="mfa-acoes">
            <button type="button" className="mfa-btn-sec" onClick={copiar}>
              <Copy size={16} /> {copiado ? t('mfa.copiado') : t('mfa.copiar')}
            </button>
            <button type="button" className="mfa-btn-sec" onClick={descarregar}>
              <Download size={16} /> {t('mfa.descarregar')}
            </button>
          </div>

          <label className="mfa-confirmo">
            <input type="checkbox" checked={guardou} onChange={(e) => setGuardou(e.target.checked)} />
            {t('mfa.guardeiOsCodigos')}
          </label>

          <button className="login-button" disabled={!guardou} onClick={() => onDone(sessao)}>
            <ShieldCheck size={20} /> {t('mfa.continuar')}
          </button>
        </div>
      </div>
    );
  }

  // ------------------------------------------------ código / configuração
  return (
    <div className="login-container">
      <div className="login-box mfa-box">
        <div className="mfa-cabecalho">
          <ShieldCheck size={28} />
          <h2>{modo === 'verificar' ? t('mfa.verificarTitulo') : t('mfa.configurarTitulo')}</h2>
        </div>

        {modo === 'configurar' && (
          <>
            <p className="mfa-texto">{t('mfa.configurarTexto')}</p>
            <div className="mfa-qr">
              {qr ? <img src={qr} alt={t('mfa.qrAlt')} /> : <div className="loading-spinner" />}
            </div>
            <button type="button" className="mfa-link" onClick={() => setMostrarSegredo(!mostrarSegredo)}>
              {t('mfa.naoConsigoLer')}
            </button>
            {mostrarSegredo && segredo && (
              <div className="mfa-segredo">
                <span>{t('mfa.chaveManual')}</span>
                <code>{segredo.match(/.{1,4}/g).join(' ')}</code>
              </div>
            )}
            <p className="mfa-texto">{t('mfa.confirmarComCodigo')}</p>
          </>
        )}

        {modo === 'verificar' && (
          <p className="mfa-texto">
            {usarRecuperacao ? t('mfa.verificarTextoRecuperacao') : t('mfa.verificarTexto')}
          </p>
        )}

        <div className="form-group">
          <input
            className="mfa-input"
            type="text"
            inputMode={usarRecuperacao ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            maxLength={usarRecuperacao ? 9 : 7}
            placeholder={usarRecuperacao ? 'xxxx-xxxx' : '000000'}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && enviar()}
          />
        </div>

        {erro && (
          <div className="error-message">
            <AlertCircle size={16} />
            {erro}
          </div>
        )}

        <button className="login-button" onClick={enviar} disabled={aEnviar || !codigo.trim()}>
          <ShieldCheck size={20} />
          {aEnviar ? t('mfa.aVerificar') : t('mfa.confirmar')}
        </button>

        {modo === 'verificar' && (
          <button
            type="button"
            className="mfa-link"
            onClick={() => { setUsarRecuperacao(!usarRecuperacao); setCodigo(''); setErro(''); }}
          >
            {usarRecuperacao ? t('mfa.usarApp') : t('mfa.usarRecuperacao')}
          </button>
        )}

        <button type="button" className="mfa-link mfa-voltar" onClick={() => onRestart('')}>
          <ArrowLeft size={14} /> {t('mfa.voltarPassword')}
        </button>
      </div>
    </div>
  );
}

export default MfaStep;
